import React, { useEffect, useState } from 'react';
import { Badge, Card, CardBody, Form, FormGroup, FormText, Input, Label } from 'reactstrap';

import { getErrorMessage } from '~libs/axiosWrapper';
import { useToast } from '~libs/toast';
import formatDate from '~support-at-home/lib/formatDate';

import { Button, ConfirmDialog, ErrorResponse, Spinner } from '~components';

import { ProdaActivationRequest } from '~support-at-home/models/prodaConnection';

import {
  useActivateProdaConnectionMutation,
  useDisconnectProdaConnectionMutation,
  useGetProdaConnectionQuery,
  useTestProdaConnectionMutation,
  useUpdateProdaConnectionDetailsMutation,
} from '~support-at-home/actions/prodaConnection';

const EXPIRY_WARNING_DAYS = 30;
const MS_PER_DAY = 86400000;
const SPINNER_SIZE = 16;

// Help blurbs — held as string literals (not JSX text) so apostrophes need no escaping.
const NAPS_BLURB =
  "The provider's National Approved Provider System (NAPS) ID from Services Australia. Used to scope Support at Home API calls. Not required to activate the device - it can be added or changed here after connecting.";
const SPID_BLURB =
  'The Services Australia Service Provider ID. Optional; stored for service calls that require it. Not needed to activate.';

const daysUntil = (value: string | null | undefined): number | null => {
  if (!value) return null;
  return Math.ceil((new Date(value).getTime() - Date.now()) / MS_PER_DAY);
};

const emptyForm: ProdaActivationRequest = {
  org_ra: '',
  device_name: '',
  naps_id: '',
  service_provider_id: '',
  dac: '',
};

const ProdaConnectionManager: React.FC = () => {
  const { data: connection, isLoading, isError, error } = useGetProdaConnectionQuery();
  const [activate, { isLoading: isActivating }] = useActivateProdaConnectionMutation();
  const [updateDetails, { isLoading: isSavingDetails }] = useUpdateProdaConnectionDetailsMutation();
  const [testConnection, { isLoading: isTesting }] = useTestProdaConnectionMutation();
  const [disconnect, { isLoading: isDisconnecting }] = useDisconnectProdaConnectionMutation();
  const { showToast } = useToast();

  const [form, setForm] = useState<ProdaActivationRequest>(emptyForm);
  const [details, setDetails] = useState({ naps_id: '', service_provider_id: '' });
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [showDisconnectConfirm, setShowDisconnectConfirm] = useState(false);

  const isConnected = connection?.status === 'connected';
  // Test / Disconnect / Save Details act on the same live PRODA connection, so they must be
  // mutually exclusive — e.g. a Test firing mid-Disconnect would toast a misleading result.
  const isBusy = isTesting || isDisconnecting || isSavingDetails;

  // Keep the editable details form in sync with the loaded connection.
  useEffect(() => {
    setDetails({ naps_id: connection?.naps_id ?? '', service_provider_id: connection?.service_provider_id ?? '' });
  }, [connection?.naps_id, connection?.service_provider_id]);

  const setField = (field: keyof ProdaActivationRequest) => (event: React.ChangeEvent<HTMLInputElement>) =>
    setForm((prev) => ({ ...prev, [field]: event.target.value }));

  const setDetailField = (field: 'naps_id' | 'service_provider_id') => (event: React.ChangeEvent<HTMLInputElement>) =>
    setDetails((prev) => ({ ...prev, [field]: event.target.value }));

  const canSubmit = Boolean(form.org_ra && form.device_name && form.dac) && !isActivating;

  const detailsDirty =
    details.naps_id !== (connection?.naps_id ?? '') ||
    details.service_provider_id !== (connection?.service_provider_id ?? '');

  const runAction = async (action: () => Promise<unknown>, successMessage: string) => {
    setErrorMessage(null);
    try {
      await action();
      showToast('success', successMessage);
      return true;
    } catch (err) {
      const message = getErrorMessage(err);
      setErrorMessage(message); // persistent, in-panel
      showToast('danger', message);
      return false;
    }
  };

  const handleActivate = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!canSubmit) return; // debounce: no re-entry while a request is in flight
    const ok = await runAction(
      () =>
        activate({
          ...form,
          naps_id: form.naps_id || null,
          service_provider_id: form.service_provider_id || null,
        }).unwrap(),
      'PRODA device activated',
    );
    if (ok) setForm(emptyForm);
  };

  const handleSaveDetails = async () => {
    if (isBusy || !detailsDirty) return;
    await runAction(
      () =>
        updateDetails({
          naps_id: details.naps_id || null,
          service_provider_id: details.service_provider_id || null,
        }).unwrap(),
      'PRODA details saved',
    );
  };

  const handleTest = async () => {
    if (isBusy) return;
    await runAction(() => testConnection().unwrap(), 'Connection verified');
  };

  const handleDisconnectConfirm = async (confirmed: boolean) => {
    setShowDisconnectConfirm(false);
    if (!confirmed || isBusy) return;
    await runAction(() => disconnect().unwrap(), 'PRODA connection removed');
  };

  if (isLoading) return <Spinner loading className="p-5" />;
  if (isError) return <ErrorResponse message={getErrorMessage(error)} />;

  if (isConnected) {
    const deviceExpiryDays = daysUntil(connection?.device_expiry);
    const showDeviceExpiryWarning = deviceExpiryDays !== null && deviceExpiryDays <= EXPIRY_WARNING_DAYS;

    return (
      <div data-cy="sah-proda-connection-status">
        {errorMessage && <ErrorResponse message={errorMessage} />}
        <Card className="mt-3">
          <CardBody>
            <div className="d-flex justify-content-between align-items-start">
              <div>
                <h5 className="mb-2">
                  <Badge color="success" className="me-2">
                    Connected
                  </Badge>
                  PRODA Device
                </h5>
                <div className="text-muted small">
                  <div>
                    <strong>Device:</strong> {connection?.device_name ?? '—'}
                  </div>
                  <div>
                    <strong>Org RA:</strong> {connection?.org_ra ?? '—'}
                  </div>
                  <div>
                    <strong>Environment:</strong> {connection?.environment ?? '—'}
                  </div>
                  <div>
                    <strong>Connected:</strong> {formatDate(connection?.connected_at)}
                  </div>
                  <div>
                    <strong>Key expiry:</strong> {formatDate(connection?.key_expiry)}
                  </div>
                  <div>
                    <strong>Device expiry:</strong> {formatDate(connection?.device_expiry)}
                  </div>
                </div>
              </div>
              <div className="d-flex flex-column gap-2">
                <Button
                  color="info"
                  size="sm"
                  onClick={handleTest}
                  disabled={isBusy}
                  loading={isTesting}
                  spinnerProps={{ loading: true, type: 'clip', size: SPINNER_SIZE }}
                  aria-label="Test Connection"
                  data-cy="sah-proda-test-btn"
                >
                  Test Connection
                </Button>
                <Button
                  color="outline-danger"
                  size="sm"
                  onClick={() => setShowDisconnectConfirm(true)}
                  disabled={isBusy}
                  data-cy="sah-proda-disconnect-btn"
                >
                  {isDisconnecting ? 'Disconnecting…' : 'Disconnect'}
                </Button>
              </div>
            </div>

            {showDeviceExpiryWarning && (
              <div className="alert alert-warning mt-3 mb-0" data-cy="sah-proda-expiry-warning">
                {deviceExpiryDays !== null && deviceExpiryDays <= 0
                  ? 'This PRODA device has expired. An authorised person must reconfirm the device in PRODA, then re-activate here.'
                  : `This PRODA device expires in ${deviceExpiryDays} day${deviceExpiryDays === 1 ? '' : 's'}. An authorised person must reconfirm the device in PRODA before it expires, then re-activate here.`}
              </div>
            )}

            {/* Provider identifiers — editable after activation (not required to activate). */}
            <hr />
            <h6>Provider Details</h6>
            <FormGroup>
              <Label for="proda-naps-edit">NAPS ID</Label>
              <Input
                id="proda-naps-edit"
                value={details.naps_id}
                onChange={setDetailField('naps_id')}
                data-cy="proda-naps-edit"
              />
              <FormText>{NAPS_BLURB}</FormText>
            </FormGroup>
            <FormGroup>
              <Label for="proda-spid-edit">Service Provider ID</Label>
              <Input
                id="proda-spid-edit"
                value={details.service_provider_id}
                onChange={setDetailField('service_provider_id')}
                data-cy="proda-spid-edit"
              />
              <FormText>{SPID_BLURB}</FormText>
            </FormGroup>
            <Button
              color="success"
              size="sm"
              onClick={handleSaveDetails}
              disabled={!detailsDirty || isBusy}
              loading={isSavingDetails}
              spinnerProps={{ loading: true, type: 'clip', size: SPINNER_SIZE }}
              aria-label="Save Details"
              data-cy="proda-save-details-btn"
            >
              Save Details
            </Button>
          </CardBody>
        </Card>
        <ConfirmDialog
          isOpen={showDisconnectConfirm}
          onClose={handleDisconnectConfirm}
          messages={[
            'This removes the stored PRODA credentials for this company.',
            'The provider will need to re-activate their device to reconnect.',
          ]}
        />
      </div>
    );
  }

  return (
    <div data-cy="sah-proda-connection-status">
      {errorMessage && <ErrorResponse message={errorMessage} />}
      <Card className="mt-3">
        <CardBody>
          <h5 className="mb-3">Activate PRODA Device</h5>
          <p className="text-muted">
            Enter the details from the provider&apos;s PRODA B2B device. The device activation code (DAC) is single-use
            and is not stored.
          </p>
          <Form autoComplete="off" onSubmit={handleActivate}>
            <FormGroup>
              <Label for="proda-org-ra">Organisation RA *</Label>
              <Input
                id="proda-org-ra"
                name="proda_org_ra"
                autoComplete="off"
                value={form.org_ra}
                onChange={setField('org_ra')}
                data-cy="proda-org-ra"
              />
            </FormGroup>
            <FormGroup>
              <Label for="proda-device">Device Name *</Label>
              <Input
                id="proda-device"
                name="proda_device_name"
                autoComplete="off"
                value={form.device_name}
                onChange={setField('device_name')}
                data-cy="proda-device"
              />
            </FormGroup>
            <FormGroup>
              <Label for="proda-naps">NAPS ID</Label>
              <Input
                id="proda-naps"
                name="proda_naps_id"
                autoComplete="off"
                value={form.naps_id ?? ''}
                onChange={setField('naps_id')}
                data-cy="proda-naps"
              />
              <FormText>{NAPS_BLURB}</FormText>
            </FormGroup>
            <FormGroup>
              <Label for="proda-spid">Service Provider ID</Label>
              <Input
                id="proda-spid"
                name="proda_service_provider_id"
                autoComplete="off"
                value={form.service_provider_id ?? ''}
                onChange={setField('service_provider_id')}
                data-cy="proda-spid"
              />
              <FormText>{SPID_BLURB}</FormText>
            </FormGroup>
            <FormGroup>
              <Label for="proda-dac">Device Activation Code *</Label>
              <Input
                id="proda-dac"
                name="proda_device_activation_code"
                type="password"
                autoComplete="new-password"
                value={form.dac}
                onChange={setField('dac')}
                data-cy="proda-dac"
              />
            </FormGroup>
            {/* `canSubmit` already carries `&& !isActivating`, so `disabled` stays true for the
                whole activation and Button's own `loading || props.disabled` is not needed here. */}
            <Button
              color="success"
              type="submit"
              disabled={!canSubmit}
              loading={isActivating}
              spinnerProps={{ loading: true, type: 'clip', size: SPINNER_SIZE }}
              aria-label="Activate Device"
              data-cy="proda-activate-btn"
            >
              Activate Device
            </Button>
          </Form>
        </CardBody>
      </Card>
    </div>
  );
};

export default ProdaConnectionManager;
